import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { Feather, ChevronLeft, ChevronRight, Lock, ArrowLeft, BookOpen, Check, Library, Heart } from 'lucide-react';

import { API } from '../config';

export default function SeriesLetterReader() {
  const { seriesId, weekNumber } = useParams();
  const { token, isSubscribed, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [letter, setLetter] = useState(null);
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRead, setIsRead] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [markingRead, setMarkingRead] = useState(false);
  const [togglingFavorite, setTogglingFavorite] = useState(false);
  const [hasReachedEnd, setHasReachedEnd] = useState(false);
  const letterEndRef = useRef(null);

  useEffect(() => {
    fetchLetter();
    setHasReachedEnd(false); // Reset when navigating to new letter
  }, [seriesId, weekNumber]);

  // Scroll detection to auto-mark as read
  useEffect(() => {
    if (!letter || !isSubscribed || isRead || hasReachedEnd) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isRead && !hasReachedEnd) {
          setHasReachedEnd(true);
          markAsRead(letter.letter_id, true);
        }
      },
      { threshold: 0.8 } // Trigger when 80% of the end marker is visible
    );

    if (letterEndRef.current) {
      observer.observe(letterEndRef.current);
    }

    return () => observer.disconnect();
  }, [letter, isSubscribed, isRead, hasReachedEnd]);

  const fetchLetter = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch series info and letter
      const [seriesRes, letterRes] = await Promise.all([
        axios.get(`${API}/series/${seriesId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        }),
        axios.get(`${API}/letters/${weekNumber}?series_id=${seriesId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        })
      ]);
      
      setSeries(seriesRes.data);
      setLetter(letterRes.data);
      setIsRead(letterRes.data.is_read || false);
      setIsFavorited(letterRes.data.is_favorited || false);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('subscription_required');
      } else if (err.response?.status === 404) {
        setError('not_found');
      } else {
        setError('unknown');
      }
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (letterId, read) => {
    if (markingRead) return;
    setMarkingRead(true);
    
    try {
      await axios.post(
        `${API}/progress/mark-read`,
        { series_id: seriesId, letter_id: letterId, is_read: read },
        { 
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true 
        }
      );
      setIsRead(read);
    } catch (error) {
      console.error('Error marking letter as read:', error);
    } finally {
      setMarkingRead(false);
    }
  };

  const toggleFavorite = async () => {
    if (togglingFavorite || !letter) return;
    setTogglingFavorite(true);
    
    try {
      const res = await axios.post(
        `${API}/favorites/toggle`,
        { series_id: seriesId, letter_id: letter.letter_id },
        { 
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true 
        }
      );
      setIsFavorited(res.data.is_favorited);
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setTogglingFavorite(false);
    }
  };

  const getLetterTypeStyles = (type) => {
    switch (type) {
      case 'diary':
        return 'diary-entry';
      case 'unsent':
        return 'unsent-letter';
      case 'interlude':
        return 'interlude';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-parchment flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <Feather className="w-10 h-10 text-wine mx-auto mb-4 animate-pulse" />
          <p className="font-lora text-charcoal-muted">Opening letter...</p>
        </div>
      </div>
    );
  }

  if (error === 'subscription_required') {
    return (
      <div className="min-h-screen bg-parchment">
        <nav className="bg-parchment border-b border-[#E5E0D0]">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Feather className="w-6 h-6 text-wine" />
              <span className="font-playfair text-xl text-charcoal">Unspoken Hearts</span>
            </Link>
          </div>
        </nav>

        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <div className="w-20 h-20 mx-auto mb-8 bg-parchment-paper border border-[#E5E0D0] rounded-full flex items-center justify-center">
            <Lock className="w-10 h-10 text-charcoal-muted" />
          </div>
          <h1 className="font-playfair text-3xl text-charcoal mb-4">This Letter is Locked</h1>
          <p className="font-lora text-charcoal-muted mb-8 max-w-md mx-auto">
            Week {weekNumber}'s letter is available exclusively to subscribers. 
            Subscribe to unlock the full story.
          </p>
          <div className="space-x-4">
            <Button 
              onClick={() => navigate(isAuthenticated ? '/subscription' : '/register')}
              className="btn-primary"
              data-testid="subscribe-to-unlock-btn"
            >
              Subscribe for $7/month
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (error === 'not_found' || !letter) {
    return (
      <div className="min-h-screen bg-parchment">
        <nav className="bg-parchment border-b border-[#E5E0D0]">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <Link to="/" className="flex items-center gap-2">
              <Feather className="w-6 h-6 text-wine" />
              <span className="font-playfair text-xl text-charcoal">Unspoken Hearts</span>
            </Link>
          </div>
        </nav>

        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <h1 className="font-playfair text-3xl text-charcoal mb-4">Letter Not Found</h1>
          <p className="font-lora text-charcoal-muted mb-8">
            This letter doesn't exist yet. Perhaps it's still being written...
          </p>
          <Button onClick={() => navigate(`/series/${seriesId}`)} className="btn-secondary">
            Return to Series
          </Button>
        </div>
      </div>
    );
  }

  const week = parseInt(weekNumber);
  const hasPrev = week > 1;
  const hasNext = week < 52;

  return (
    <div className="min-h-screen bg-parchment">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-parchment/95 backdrop-blur-sm border-b border-[#E5E0D0]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link 
            to={`/series/${seriesId}`}
            className="inline-flex items-center gap-2 text-charcoal-muted hover:text-charcoal transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-lora">{series?.title || 'Series'}</span>
          </Link>
          
          <div className="flex items-center gap-2">
            <Feather className="w-5 h-5 text-wine" />
            <span className="font-playfair text-lg text-charcoal">Week {week}</span>
          </div>

          <div className="flex items-center gap-2">
            {/* Favorite Toggle */}
            {isAuthenticated && (
              <button
                onClick={toggleFavorite}
                disabled={togglingFavorite}
                className={`p-2 rounded-full transition-all ${
                  isFavorited 
                    ? 'bg-rose-100 text-rose-500 hover:bg-rose-200' 
                    : 'bg-parchment-paper text-charcoal-light hover:bg-parchment-subtle hover:text-rose-400'
                }`}
                title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                data-testid="favorite-toggle"
              >
                <Heart className={`w-5 h-5 ${isFavorited ? 'fill-current' : ''} ${togglingFavorite ? 'animate-pulse' : ''}`} />
              </button>
            )}

            {/* Mark as Read Toggle */}
            {isSubscribed && (
              <button
                onClick={() => markAsRead(letter.letter_id, !isRead)}
                disabled={markingRead}
                className={`p-2 rounded-full transition-all ${
                  isRead 
                    ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                    : 'bg-parchment-paper text-charcoal-light hover:bg-parchment-subtle hover:text-charcoal'
                }`}
                title={isRead ? 'Mark as unread' : 'Mark as read'}
                data-testid="mark-read-toggle"
              >
                <Check className={`w-5 h-5 ${markingRead ? 'animate-pulse' : ''}`} />
              </button>
            )}

            {hasPrev && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/series/${seriesId}/letter/${week - 1}`)}
                className="text-charcoal-muted hover:text-charcoal"
                data-testid="prev-letter-btn"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
            )}
            {hasNext && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/series/${seriesId}/letter/${week + 1}`)}
                className="text-charcoal-muted hover:text-charcoal"
                data-testid="next-letter-btn"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            )}
          </div>
        </div>
      </nav>

      {/* Letter Content */}
      <main className="max-w-3xl mx-auto px-6 py-12 md:py-20">
        <article className={`animate-fade-in ${getLetterTypeStyles(letter.letter_type)}`}>
          {/* Read Status Badge */}
          {isSubscribed && isRead && (
            <div className="flex justify-center mb-6">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-lora">
                <Check className="w-4 h-4" />
                Read
              </span>
            </div>
          )}

          {/* Header */}
          <header className="text-center mb-12 pb-8 border-b border-[#E5E0D0]">
            <p className="font-lora text-sm text-wine mb-2">{series?.era}</p>
            <p className="font-dancing text-2xl text-sepia mb-3">{letter.date}</p>
            <h1 className="font-playfair text-3xl md:text-4xl text-charcoal mb-4" data-testid="letter-title">
              {letter.title}
            </h1>
            <div className="flex items-center justify-center gap-4 text-charcoal-muted">
              <span className="font-lora text-sm">Week {letter.week_number}</span>
              {letter.pov && (
                <>
                  <span className="text-[#E5E0D0]">•</span>
                  <span className="font-lora text-sm">{letter.pov}'s {letter.letter_type}</span>
                </>
              )}
            </div>
          </header>

          {/* Content */}
          <div className="letter-content" data-testid="letter-content">
            {letter.content.split('\n\n').map((paragraph, idx) => (
              <p key={idx} className="font-lora text-charcoal text-lg leading-relaxed mb-6">
                {paragraph}
              </p>
            ))}
          </div>

          {/* End of letter marker for scroll detection */}
          <div ref={letterEndRef} className="h-4" aria-hidden="true" />

          {/* Auto-read notification */}
          {isSubscribed && hasReachedEnd && !isRead && (
            <div className="flex justify-center mb-4 animate-fade-in">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-600 rounded-full text-sm font-lora">
                <Check className="w-4 h-4" />
                Marked as read
              </span>
            </div>
          )}

          {/* Footer */}
          <footer className="mt-16 pt-8 border-t border-[#E5E0D0]">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
              <p className="font-dancing text-xl text-sepia italic">
                "Moments preserved in ink"
              </p>
              
              <div className="flex items-center gap-4">
                {hasPrev && (
                  <Button
                    onClick={() => navigate(`/series/${seriesId}/letter/${week - 1}`)}
                    className="btn-secondary"
                    data-testid="footer-prev-btn"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                )}
                {hasNext && (
                  <Button
                    onClick={() => navigate(`/series/${seriesId}/letter/${week + 1}`)}
                    className="btn-primary"
                    data-testid="footer-next-btn"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>
            </div>

            <div className="mt-8 flex justify-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate(`/series/${seriesId}`)}
                className="font-lora text-charcoal-muted hover:text-charcoal"
                data-testid="view-series-btn"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                View Series
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/library')}
                className="font-lora text-charcoal-muted hover:text-charcoal"
                data-testid="view-library-btn"
              >
                <Library className="w-4 h-4 mr-2" />
                Library
              </Button>
            </div>
          </footer>
        </article>
      </main>
    </div>
  );
}
